import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ProgressSteps from './components/ProgressSteps';
import { DatabaseConfig } from '../../types/database.type';
import Step1EngineSelection from './steps/Step1DB';
import Step2Configuration from './steps/Step2DB';
import Step3Security from './steps/Step3DB';
import Step4Review from './steps/Step4DB';
import { productsService, ordersService, pricingService } from '../../services';
import { PricingPlan } from '../../services/pricing.service';
import { ProductCategory } from '../../constants/productCategory';
import { Product } from '../../types/product.type';
import { databaseEngines } from '../../constants/databaseEngine';

// El id del motor en el wizard no siempre coincide con el osType del catálogo
// de precios (FAMILY#RDS). En particular 'postgresql' -> 'POSTGRES'.
const ENGINE_TO_OSTYPE: Record<string, string> = {
  postgresql: 'POSTGRES',
  mysql: 'MYSQL',
  mariadb: 'MARIADB',
  sqlserver: 'SQLSERVER',
  mongodb: 'MONGODB',
};

const engineOsType = (engine: string): string =>
  ENGINE_TO_OSTYPE[engine] || (engine || '').toUpperCase();


const CreateDatabase: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<DatabaseConfig>({
    name: '',
    engine: '',
    version: '',
    instanceType: '',
    storage: 20,
    backups: true,
    multiAZ: false,
    publicAccess: false,
    port: 5432,
    username: '',
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Ref para evitar llamadas duplicadas (React.StrictMode ejecuta efectos dos veces en desarrollo)
  const hasFetchedProducts = useRef(false);

  // Obtener productos de la categoría DATABASE al cargar el componente (igual que VM)
  useEffect(() => {
    if (hasFetchedProducts.current) return;
    hasFetchedProducts.current = true;

    const fetchProducts = async () => {
      try {
        const response = await productsService.getProductsByCategory(ProductCategory.DATABASE);
        const productsArray = Array.isArray(response?.products) ? response.products : [];
        setProducts(productsArray);
      } catch (error) {
        console.error('Error fetching Database products:', error);
        setProducts([]);
        hasFetchedProducts.current = false;
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!config.engine) return;
    setSelectedPlanCode(null);
    setPlans([]);
    setConfig(prev => ({ ...prev, instanceType: '', planCode: undefined }));

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const fetched = await pricingService.getPlans('RDS', engineOsType(config.engine), '');
        setPlans(fetched);
        if (fetched.length > 0) {
          const first = [...fetched].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99))[0];
          setSelectedPlanCode(first.planCode);
          const instanceClass = (first.providerConfiguration as any)?.instanceClass || first.planCode;
          const planStorage = first.includedResources?.storageGb;
          setConfig(prev => ({
            ...prev,
            instanceType: instanceClass,
            planCode: first.planCode,
            ...(planStorage ? { storage: planStorage } : {}),
          }));
        }
      } catch {
        setPlans([]);
        console.error('No se pudieron cargar los planes para el motor seleccionado. Intenta de nuevo.');
      } finally {
        setIsLoadingPlans(false);
      }
    };
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.engine]);

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    if (!config.engine || !config.version || !config.instanceType) {
      console.error('Missing required configuration:', { engine: config.engine, version: config.version, instanceType: config.instanceType });
      return;
    }
    setIsCreating(true);
    try {
      // Filtrar productos por engine. Normalizamos quitando espacios/guiones para
      // que "sqlserver" haga match con "RDS SQL Server Web" (el nombre tiene espacio).
      const norm = (s: string) => (s || '').toLowerCase().replace(/[\s_-]/g, '');
      const engineKey = norm(config.engine);
      const productsByEngine = products.filter(p => {
        const pEngine = norm((p as { engine?: string }).engine || '');
        return (
          norm(p.id).includes(engineKey) ||
          (!!pEngine && (pEngine.includes(engineKey) || engineKey.includes(pEngine))) ||
          norm(p.name).includes(engineKey) ||
          norm(p.description || '').includes(engineKey)
        );
      });
      const productId = productsByEngine[0]?.id || products[0]?.id;

      if (!productId) {
        console.error('No product found for database');
        alert('No se encontró un producto de base de datos. Verifica que existan productos en la categoría DATABASE.');
        setIsCreating(false);
        return;
      }

      // El backend (platform-orders) exige metadata.pricingSelection para cotizar
      // la orden. Sin planCode no hay plan que cotizar -> abortar con mensaje claro
      // en vez de provocar un 500.
      if (!config.planCode) {
        alert('No hay un plan disponible para este motor. Selecciona un plan antes de continuar.');
        setIsCreating(false);
        return;
      }

      const selectedEngine = databaseEngines.find((e) => e.id === config.engine);
      const engineName = selectedEngine ? `${selectedEngine.name} ${config.version}` : `${config.engine} ${config.version}`;

      // Crear la orden (estructura similar a VM, enviada al endpoint /orders del API)
      const orderData = {
        productId,
        quantity: 1,
        metadata: {
          type: 'DATABASE',
          // Requerido por platform-orders para cotizar (FAMILY#RDS del catálogo).
          pricingSelection: {
            typeFamily: 'RDS',
            osType: engineOsType(config.engine),
            osFamily: 'GENERIC',
            planCode: config.planCode,
            addons: [],
          },
          databaseConfig: {
            name: config.name,
            engine: config.engine,
            engineName,
            version: config.version,
            instanceType: config.instanceType,
            planCode: config.planCode,
            storage: config.storage,
            backups: config.backups,
            multiAZ: config.multiAZ,
            publicAccess: config.publicAccess,
            port: config.port,
            username: config.username || '',
          },
        },
      };

      const createdOrder = await ordersService.create(orderData);

      if (!createdOrder?.id) {
        throw new Error('La API no devolvió el ID de la orden');
      }

      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error: any) {
      console.error('Error creating order:', error);
      const message = error?.message || 'Error al crear la orden. Por favor, intenta de nuevo.';
      alert(message);
      setIsCreating(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return config.name && config.engine && config.version;
      case 2:
        return config.instanceType;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <Step1EngineSelection config={config} setConfig={setConfig} />;
      case 2:
        return (
          <Step2Configuration
            config={config}
            setConfig={setConfig}
            plans={plans}
            selectedPlanCode={selectedPlanCode}
            isLoadingPlans={isLoadingPlans}
            onSelectPlan={(planCode, instanceClass) => {
              setSelectedPlanCode(planCode);
              const plan = plans.find(p => p.planCode === planCode);
              const planStorage = plan?.includedResources?.storageGb;
              setConfig(prev => ({
                ...prev,
                instanceType: instanceClass,
                planCode,
                ...(planStorage ? { storage: planStorage } : {}),
              }));
            }}
          />
        );
      case 3:
        return <Step3Security config={config} setConfig={setConfig} />;
      case 4:
        return <Step4Review config={config} plans={plans} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/databases')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">Back to Databases</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create Database</h1>
        <p className="text-gray-600 dark:text-gray-400">Deploy a managed database instance</p>
      </div>

      {/* Progress Steps */}
      <ProgressSteps currentStep={step} />

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/databases')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          {step < 4 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-dark-bg dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-dark-bg dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creando orden...' : 'Create Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateDatabase;
